import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { AuthBrandPanel } from "@/components/AuthBrandPanel";
import { Wrench, CheckCircle2, XCircle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => nav("/login"), 3000);
    return () => clearTimeout(t);
  }, [done, nav]);

  const submit = async (e) => {
    e?.preventDefault();
    if (pw !== pw2) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      <AuthBrandPanel />
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <Wrench className="w-5 h-5 text-primary-foreground" />
            </div>
            <div className="font-display font-black text-2xl uppercase">SynqMoto<span className="text-primary"> ERP</span></div>
          </div>

          {!token ? (
            <div data-testid="reset-invalid">
              <XCircle className="w-10 h-10 text-destructive mb-4"/>
              <h2 className="font-display font-black text-4xl uppercase tracking-tighter mb-3">Invalid link</h2>
              <p className="text-muted-foreground text-sm mb-8">This reset link is missing its token. Please request a fresh one.</p>
              <Link to="/forgot-password" data-testid="reset-request-new">
                <Button className="w-full h-11 rounded-sm bg-primary hover:bg-orange-600 text-primary-foreground uppercase tracking-[0.15em] font-bold text-sm">Request New Link</Button>
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-success">
              <CheckCircle2 className="w-10 h-10 text-primary mb-4"/>
              <h2 className="font-display font-black text-4xl uppercase tracking-tighter mb-3">Password updated</h2>
              <p className="text-muted-foreground text-sm mb-8">Your password has been changed. Redirecting you to sign in…</p>
              <Link to="/login" data-testid="reset-goto-login">
                <Button className="w-full h-11 rounded-sm bg-primary hover:bg-orange-600 text-primary-foreground uppercase tracking-[0.15em] font-bold text-sm">Go to Sign In</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">Account Recovery</div>
              <h2 className="font-display font-black text-4xl uppercase tracking-tighter mb-3">Reset password</h2>
              <p className="text-muted-foreground text-sm mb-8">Choose a new password for your account.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">New Password</Label>
                  <Input data-testid="reset-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                    className="mt-2 rounded-sm bg-secondary border-border font-mono text-sm h-11" placeholder="••••••••" required />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Confirm New Password</Label>
                  <Input data-testid="reset-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                    className="mt-2 rounded-sm bg-secondary border-border font-mono text-sm h-11" placeholder="••••••••" required />
                </div>
                <Button data-testid="reset-submit" type="submit" disabled={busy || !pw || !pw2}
                  className="w-full h-11 rounded-sm bg-primary hover:bg-orange-600 text-primary-foreground uppercase tracking-[0.15em] font-bold text-sm">
                  {busy ? "Updating…" : "Update Password →"}
                </Button>
              </form>
              <Link to="/login" data-testid="reset-back-link" className="mt-6 inline-block text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors">
                Back to Sign In
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
